// The chat's state: every run's timeline, built from the server's run stream (protocol 2, app/hub.py) and from
// history. Pure functions, no React: tests replay real streams through them (timeline.test.ts).
//
// The stream's guarantee: a run's messages carry seq 1, 2, ... in the order they happened. `apply` takes them in that
// order only: one it already has is ignored (a repeat after a reconnect), one ahead of a gap waits until the gap is
// filled (by a `resume`) or reset. Every change returns new objects for what changed and the same objects for the
// rest, so a row redraws only when its own data changed.

export const LIVE_LINES = 20;                        // activity lines kept per step while it runs (the rest are counted)

export type Line = { seq: number; cls: "progress" | "milestone" | "warning"; kind: string; label: string;
  level: "info" | "warn" | "error"; at: number; count: number };
export type Lines = { lines: Line[]; hidden: number };
export type ToolItem = { n: number; kind: "tool"; tool: string; input?: Record<string, unknown>; verdict?: string;
  text?: string; ms?: number; image?: string; shot?: string; done: boolean; skipped?: boolean; startedAt?: number;
  act: Lines };
export type TextItem = { n: number; kind: "text"; text: string; streaming?: boolean };   // streaming: being written
export type Item = ToolItem | TextItem;
export type Stage = { on: boolean; since: number; seconds?: number; parts?: Record<string, number>; fresh?: boolean; act: Lines };
export type Run = {
  id: string; conversation?: string; message: string; items: Item[]; status?: string; summary?: string;
  started: number; ended?: number; verdict?: "up" | "down" | null; note?: string | null;
  seq: number;                                      // the last message applied in order
  ahead: Msg[];                                     // messages past a gap, waiting for it to be filled
  think: { on: boolean; since: number; brain?: string };
  stages: { startup?: Stage; cleanup?: Stage; thinking?: Stage };
  lastAt: number;                                   // when anything last arrived for it (stall detection)
  lastLabel?: string;
  needsHistory?: boolean;                           // a reset: the finished steps come from history
  readyS?: number | null;                           // how long its session took to get ready (from history)
};
export type Timeline = { order: string[]; byId: Record<string, Run> };
export type Msg = { t: string; run: string; seq?: number; [k: string]: unknown };
export type Need = { resume?: { run: string; seq: number }; history?: string };

const NO_LINES: Lines = { lines: [], hidden: 0 };
export const empty = (): Timeline => ({ order: [], byId: {} });

function newRun(id: string, now: number): Run {
  return { id, message: "", items: [], started: now, seq: 0, ahead: [], think: { on: false, since: now },
    stages: {}, lastAt: now };
}

function put(tl: Timeline, r: Run): Timeline {
  const known = r.id in tl.byId;
  return { order: known ? tl.order : [...tl.order, r.id], byId: { ...tl.byId, [r.id]: r } };
}

function upsertItem(items: Item[], it: Item): Item[] {
  const i = items.findIndex((x) => x.n === it.n);
  if (i >= 0) { const out = items.slice(); out[i] = it; return out; }
  const out = [...items, it];
  return out[out.length - 2] && out[out.length - 2].n > it.n ? out.sort((a, b) => a.n - b.n) : out;
}

function addLine(l: Lines, line: Line, merge: boolean, prev: unknown): Lines {
  const last = l.lines[l.lines.length - 1];
  if (merge && last && last.seq === prev) return { lines: [...l.lines.slice(0, -1), line], hidden: l.hidden };
  const lines = [...l.lines, line];
  return lines.length > LIVE_LINES ? { lines: lines.slice(1), hidden: l.hidden + 1 } : { lines, hidden: l.hidden };
}

const tool = (r: Run, n: number) => r.items.find((x) => x.n === n && x.kind === "tool") as ToolItem | undefined;

// one message, in order
function step(r: Run, m: Msg, now: number): Run {
  const t = m.t, kind = m.kind as string;
  r = { ...r, seq: m.seq as number, lastAt: now };
  if (t === "ev") {
    const n = m.n as number;
    if (kind === "run.start") {
      return { ...r, message: String(m.text ?? r.message), conversation: (m.conversation as string) ?? r.conversation, started: now };
    }
    if (kind === "tool.start") {
      const had = tool(r, n);
      return { ...r, lastLabel: undefined, items: upsertItem(r.items, { n, kind: "tool", tool: String(m.tool), act: had?.act ?? NO_LINES,
        input: m.input as Record<string, unknown>, done: false, startedAt: now }) };
    }
    if (kind === "tool.end") {
      const had = tool(r, n);
      return { ...r, lastLabel: undefined, items: upsertItem(r.items, { ...(had ?? { n, kind: "tool", act: NO_LINES }),
        n, kind: "tool", tool: String(m.tool ?? had?.tool ?? ""), input: (had?.input ?? m.input) as Record<string, unknown>,
        done: true, verdict: String(m.verdict ?? ""), text: String(m.text ?? ""), ms: m.ms as number,
        image: m.image as string | undefined, shot: m.shot as string | undefined }) };
    }
    if (kind === "tool.skipped") {
      return { ...r, items: upsertItem(r.items, { n, kind: "tool", tool: String(m.tool), done: true, skipped: true,
        text: String(m.why ?? ""), act: NO_LINES }) };
    }
    if (kind === "approval") {                       // a phone chat's decision on a step that cannot be undone
      const said = m.ok ? "approved" : "declined";
      return { ...r, items: upsertItem(r.items, { n, kind: "tool", tool: "approval", done: true, verdict: said,
        input: { action: m.action, summary: m.summary }, text: `${String(m.summary ?? "")} - ${said} by ${String(m.by ?? "")}`,
        act: NO_LINES }) };
    }
    if (kind === "model.delta") {                    // a piece of the text being written: appended
      const had = r.items.find((x) => x.n === n && x.kind === "text") as TextItem | undefined;
      const text = m.replace ? String(m.text ?? "") : (had?.text ?? "") + String(m.text ?? "");
      return { ...r, items: upsertItem(r.items, { n, kind: "text", text, streaming: true }) };
    }
    if (kind === "model.text") return { ...r, items: upsertItem(r.items, { n, kind: "text", text: String(m.text ?? "") }) };
    if (kind === "run.end") {
      const stages = { ...r.stages };
      for (const k of Object.keys(stages) as (keyof Run["stages"])[]) if (stages[k]?.on) stages[k] = { ...stages[k]!, on: false };
      return { ...r, status: String(m.status), summary: String(m.summary ?? ""), ended: now,
        think: { ...r.think, on: false }, stages, lastLabel: undefined };
    }
    return r;
  }
  if (t === "act") {
    const line: Line = { seq: m.seq as number, cls: m.class as Line["cls"], kind: String(m.kind), label: String(m.label),
      level: (m.level as Line["level"]) ?? "info", at: Number(m.at ?? 0), count: Number(m.count ?? 1) };
    r = { ...r, lastLabel: line.label };
    if (typeof m.item === "number") {
      const n = m.item, had = tool(r, n) ?? { n, kind: "tool" as const, tool: "", done: false, act: NO_LINES, startedAt: now };
      return { ...r, items: upsertItem(r.items, { ...had, act: addLine(had.act, line, !!m.merge, m.prev) }) };
    }
    const sk = (m.stage as keyof Run["stages"]) || "thinking";
    const st = r.stages[sk] ?? { on: sk === "thinking", since: now, act: NO_LINES };
    return { ...r, stages: { ...r.stages, [sk]: { ...st, act: addLine(st.act, line, !!m.merge, m.prev) } } };
  }
  if (t === "think") {
    const on = !!m.on;
    // the thinking stage's lines belong to one spell of thinking: a new spell starts empty
    const stages = on ? { ...r.stages, thinking: { on: true, since: now, act: NO_LINES } } : r.stages;
    return { ...r, think: { on, since: now, brain: (m.brain as string) ?? r.think.brain }, stages, lastLabel: on ? undefined : r.lastLabel };
  }
  if (t === "stage") {
    const sk = m.stage as "startup" | "cleanup";
    const st = r.stages[sk];
    const next: Stage = m.on ? { on: true, since: now, fresh: m.fresh as boolean | undefined, act: st?.act ?? NO_LINES }
      : { ...(st ?? { since: now, act: NO_LINES }), on: false, seconds: m.seconds as number | undefined,
          parts: (m.parts as Record<string, number>) ?? st?.parts };
    return { ...r, stages: { ...r.stages, [sk]: next }, lastLabel: m.on ? undefined : r.lastLabel };
  }
  return r;
}

/** Apply one message from the socket. Returns the new timeline and what the page must ask for (a resume after a gap,
 *  the history after a reset). Messages without seq are not run messages and are ignored here. */
export function apply(tl: Timeline, m: Msg, now = Date.now()): { tl: Timeline; need?: Need } {
  if (m.t === "reset") {
    let r = tl.byId[m.run] ?? newRun(m.run, now);
    const seq = Number(m.seq ?? 0);
    const open = m.open as { item: number; tool: string; input?: Record<string, unknown> } | null | undefined;
    r = { ...r, seq: Math.max(r.seq, seq), needsHistory: true, think: { on: !!m.thinking, since: now, brain: r.think.brain } };
    if (open) r = { ...r, items: upsertItem(r.items, { n: open.item, kind: "tool", tool: open.tool, input: open.input,
      done: false, startedAt: now, act: tool(r, open.item)?.act ?? NO_LINES }) };
    const text = m.text as { n: number; text: string } | null | undefined;
    if (text) r = { ...r, items: upsertItem(r.items, { n: text.n, kind: "text", text: text.text, streaming: true }) };
    if (m.stage === "startup" || m.stage === "cleanup") {
      r = { ...r, stages: { ...r.stages, [m.stage as string]: { on: true, since: now, act: NO_LINES } } };
    }
    tl = put(tl, { ...r, ahead: [] });
    const waiting = r.ahead.filter((x) => (x.seq as number) > r.seq);
    for (const x of waiting) tl = apply(tl, x, now).tl;
    return { tl, need: { history: m.run } };
  }
  if (typeof m.seq !== "number") return { tl };
  let r = tl.byId[m.run] ?? newRun(m.run, now);
  if (m.seq <= r.seq) return { tl };                                  // a repeat
  if (m.seq > r.seq + 1) {                                            // a gap: hold it, ask for what is missing
    if (r.ahead.some((x) => x.seq === m.seq)) return { tl };
    const ahead = [...r.ahead, m].sort((a, b) => (a.seq as number) - (b.seq as number));
    return { tl: put(tl, { ...r, ahead }), need: { resume: { run: m.run, seq: r.seq } } };
  }
  r = step(r, m, now);
  while (r.ahead.length && r.ahead[0].seq === r.seq + 1) {
    const [next, ...rest] = r.ahead;
    r = step({ ...r, ahead: rest }, next, now);
  }
  r = { ...r, ahead: r.ahead.filter((x) => (x.seq as number) > r.seq) };
  return { tl: put(tl, r) };
}

export type HistoryRun = { id: string; message: string; status: string; summary: string; started_at: number;
  ended_at: number | null; verdict?: "up" | "down" | null; verdict_note?: string | null; ready_s?: number | null;
  steps: { n: number; tool: string; input: Record<string, unknown>; verdict: string; text: string; ms: number | null;
    shot?: string | null }[] };

function itemOf(s: HistoryRun["steps"][number]): Item {
  return s.tool === "" ? { n: s.n, kind: "text", text: s.text }
    : { n: s.n, kind: "tool", tool: s.tool, input: s.input, verdict: s.verdict, text: s.text, ms: s.ms ?? undefined,
        shot: s.shot ?? undefined, done: true, skipped: s.verdict === "skipped", act: NO_LINES };
}

/** Merge a conversation's history: runs not seen live come from it whole; for a run seen live, history fills in
 *  the steps the stream did not deliver (after a reset) and never replaces what the stream did. */
export function withHistory(tl: Timeline, conversation: string, runs: HistoryRun[], now = Date.now()): Timeline {
  for (const h of runs) {
    const live = tl.byId[h.id];
    const finished = h.status !== "running";
    if (!live) {
      tl = put(tl, { ...newRun(h.id, h.started_at * 1000), conversation, message: h.message, seq: 0,
        status: finished ? h.status : undefined, summary: h.summary, started: h.started_at * 1000,
        ended: h.ended_at ? h.ended_at * 1000 : undefined, verdict: h.verdict, note: h.verdict_note,
        readyS: h.ready_s ?? null, items: h.steps.map(itemOf), lastAt: now });
      continue;
    }
    // a step with a history row has finished (rows are written when a step ends): it fills a step the stream did
    // not deliver, and replaces one the stream still shows running (its end came before a reset), keeping its lines
    let items = live.items;
    for (const s of h.steps) {
      const had = items.find((x) => x.n === s.n);
      if (!had) items = upsertItem(items, itemOf(s));
      else if (had.kind === "tool" && !had.done) {
        const fin = itemOf(s);
        items = upsertItem(items, fin.kind === "tool" ? { ...fin, act: had.act } : fin);
      }
    }
    tl = put(tl, { ...live, conversation, message: live.message || h.message, items, needsHistory: false, readyS: h.ready_s ?? live.readyS,
      verdict: h.verdict ?? live.verdict, note: h.verdict_note ?? live.note,
      status: live.status ?? (finished ? h.status : undefined), summary: live.status ? live.summary : finished ? h.summary : live.summary,
      ended: live.ended ?? (h.ended_at ? h.ended_at * 1000 : undefined) });
  }
  return tl;
}

/** The runs this page still shows going: after a reconnect each is asked for again, since its run.end may have been
 *  sent while the page was away (the server's hello names only the run going now, if any). */
export function unfinished(tl: Timeline): { run: string; seq: number }[] {
  return tl.order.map((id) => tl.byId[id]).filter((r) => !r.status).map((r) => ({ run: r.id, seq: r.seq }));
}

/** The runs of one conversation, oldest first. */
export function runsOf(tl: Timeline, conversation: string | null): Run[] {
  if (!conversation) return [];
  return tl.order.map((id) => tl.byId[id]).filter((r) => r.conversation === conversation)
    .sort((a, b) => a.started - b.started);
}
